// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { mapLinks } from "@flip/Overrides/Widgets/Common/HeaderSignIn";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { Typography } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    leftMargin = "leftMargin",
    marginBottom = "marginBottom",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.leftMargin]: number;
        [fields.marginBottom]: number;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    // XNG-Changes
    const pageLinks = getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Common/HeaderSignIn";
    })?.fields as any;

    if (!pageLinks) {
        return {};
    }
    const orderManagementLinks = mapLinks(state, pageLinks?.orderManagementLinks);
    const accountManagementLinks = mapLinks(state, pageLinks?.accountManagementLinks);
    const linkGroups = [
        { title: pageLinks.accountManagementTitle, links: accountManagementLinks },
        { title: pageLinks.orderManagementSectionTitle, links: orderManagementLinks },
    ];
    return {
        linkGroups,
    };
};

// XNG-Changes
interface ListStyleProp {
    leftMargin: number;
    marginBottom: number;
}

// XNG-Changes
export interface MyAccountDashboardLinksStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

const myAccountDashboardLinksStyles: MyAccountDashboardLinksStyles = {
    title: {
        variant: "h6",
        forwardAs: "h2"
    },
    link: {
        typographyProps: {
            size: 14,
            lineHeight: "26px",
        },
        css: css`
            display: block;
            margin-bottom: 3px;
        `,
    },
    container: {
        css: css`
            margin-bottom: 20px;
        `,
    },
};
const styles = myAccountDashboardLinksStyles;

const ListStyle = styled.ul<ListStyleProp>`
    margin-left: ${props => props.leftMargin}px;
    li {
        margin-bottom: ${props => props.marginBottom}px;
    }
`;

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const MyAccountDashboardLinks = (props: Props) => {
    // XNG-Changes
    if (!props.linkGroups) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${props.fields.customCSS}
        `,
    };

    // XNG-Changes
    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper
                className="navigation-list"
                data-test-selector="MyAccountDashboardLinks"
            >
                {props.linkGroups?.map((linkGroup: any, index: any) => (
                    <StyledWrapper key={index} {...styles.container} className="navigation-list-item">
                        {linkGroup.links.length > 0 && linkGroup.title && (
                            <Typography {...styles.title}>{linkGroup.title}</Typography>
                        )}
                        {linkGroup.links.length > 0 &&
                            linkGroup.links.map(
                                (link: any) =>
                                    link?.url && (
                                        <Link
                                            className="navigation-list-link"
                                            key={`${link?.title}_${link?.url}`}
                                            href={link?.url}
                                            {...styles.link}
                                            target={(link as any).openInNewWindow === true ? "_blank" : ""}
                                            aria-label={link?.title}
                                        >
                                            {link?.title}
                                        </Link>
                                    ),
                            )}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.navigation-list{
}

.navigation-list-item {
}

.navigation-list-link {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyAccountDashboardLinks),
    definition: {
        group: "Basic",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.leftMargin,
                displayName: "Left Margin",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 100,
                min: 0,
                tab: basicTab,
            },
            {
                name: fields.marginBottom,
                displayName: "Margin Bottom",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 100,
                min: 0,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
